import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { Globe, Edit2, Trash2, AlertCircle } from 'lucide-react';
import ExecutionStatusOverlay from './ExecutionStatusOverlay';
import { getExecutionBorderClass, type ExecutionStatus } from './executionStatus';

interface HTTPStepNodeData {
  stepName: string;
  url?: string;
  method?: string;
  description?: string;
  continueOnError?: boolean;
  condition?: string;
  timeout?: number;
  executionStatus?: ExecutionStatus;
  onEdit?: () => void;
  onDelete?: () => void;
}

function HTTPStepNode({ data, selected }: NodeProps<HTTPStepNodeData>) {
  const methodColor = {
    GET: 'bg-green-500',
    POST: 'bg-blue-500',
    PUT: 'bg-yellow-500',
    DELETE: 'bg-red-500',
    PATCH: 'bg-purple-500',
  }[data.method || 'POST'] || 'bg-gray-500';

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[280px] max-w-[320px] relative ${
        selected ? 'border-green-500 shadow-green-200' : 'border-green-300'
      } ${getExecutionBorderClass(data.executionStatus)}`}
    >
      <ExecutionStatusOverlay status={data.executionStatus} />
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 !bg-green-500"
      />

      {/* Header */}
      <div className={`bg-gradient-to-r ${methodColor} to-green-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          <span className="font-semibold text-sm">HTTP Step</span>
        </div>
        <div className="flex gap-1 nodrag nopan" style={{ pointerEvents: 'auto' }}>
          {data.onEdit && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-green-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('✏️ Edit clicked!', data.stepName);
                if (data.onEdit) {
                  data.onEdit();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Edit node"
            >
              <Edit2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
          {data.onDelete && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-red-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('🗑️ Delete clicked!', data.stepName);
                if (data.onDelete) {
                  data.onDelete();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Delete node"
            >
              <Trash2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-gray-900">{data.stepName}</div>
            {data.continueOnError && (
              <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                <AlertCircle className="w-3 h-3 mr-1" />
                Continue on Error
              </Badge>
            )}
          </div>
          {data.description && (
            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
              {data.description}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t">
          <Badge variant="secondary" className="text-xs">
            {data.method || 'POST'}
          </Badge>
          {data.timeout && (
            <span className="text-xs text-gray-500">{data.timeout}ms</span>
          )}
        </div>

        {data.url && (
          <div className="text-xs text-gray-500 truncate" title={data.url}>
            {data.url.replace(/^https?:\/\//, '').substring(0, 40)}...
          </div>
        )}

        {data.condition && (
          <div className="text-xs text-gray-400 italic mt-1">
            Condition: {data.condition.substring(0, 30)}...
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-green-500"
      />
    </div>
  );
}

export default memo(HTTPStepNode);

